import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { linkWebSubscription } from '../lib/webSubscription';
import { track } from '../lib/analytics';

// Reloading or revisiting the return page links the same checkout again; count each checkout once.
function trackPurchaseOnce(sessionId) {
  const key = `lingo_purchase_tracked_${sessionId}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
  } catch {
    // Storage blocked: count it anyway.
  }
  track('purchase_completed', { platform: 'web' });
}

function SubscriptionReturnPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loading, getToken, refreshUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const sessionId = searchParams.get('session_id');

  const handleLink = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      await linkWebSubscription(getToken(), { sessionId });
      trackPurchaseOnce(sessionId);
      await refreshUser();
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to link your subscription');
    } finally {
      setSubmitting(false);
    }
  }, [getToken, refreshUser, sessionId]);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    if (!loading && isAuthenticated && sessionId && !success && !submitting) {
      handleLink();
    }
  }, [loading, isAuthenticated, sessionId, success, submitting, handleLink]);

  if (loading || submitting) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Activating your subscription...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-xl mx-auto bg-card rounded-lg shadow-xl border border-border p-6 sm:p-8">
        <h1 className="text-2xl font-bold text-foreground mb-4">Subscription Activation</h1>

        {success ? (
          <div className="space-y-4">
            <div className="bg-success/20 border border-success text-success px-4 py-3 rounded">
              Your premium subscription is now active.
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => navigate('/account')}
                className="bg-primary text-primary-foreground px-4 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
              >
                Go to Account
              </button>
              <Link
                to="/dashboard"
                className="px-4 py-3 border border-border text-foreground rounded-lg hover:bg-muted transition-colors text-center"
              >
                Go to Dashboard
              </Link>
            </div>
          </div>
        ) : error ? (
          <div className="space-y-4">
            <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-4 py-3 rounded">
              {error}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleLink}
                disabled={!sessionId}
                className="bg-primary text-primary-foreground px-4 py-3 rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                Retry
              </button>
              <button
                type="button"
                onClick={() => navigate('/account')}
                className="px-4 py-3 border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
              >
                Go to Account
              </button>
            </div>
          </div>
        ) : (
          <div className="text-muted-foreground">
            {sessionId ? 'Processing...' : 'No checkout session found. Please try upgrading again from your account page.'}
          </div>
        )}
      </div>
    </div>
  );
}

export default SubscriptionReturnPage;
